/**
 * BL-0472 — lecture index / plan / historique via proxy `GET …/pdf-file`
 * (NAS si ACK catalogue, sinon cache S3). Helpers purs + ouverture blob.
 */

/** Types dont la vérité fichier est NAS (proxy pdf-file), pas signUrl S3. */
export const U3141_NAS_TRUTH_DOCUMENT_TYPES = [
  'rfLotDetailIndex',
  'rfLotDetailPlan',
  'rfLotHistoriqueCadastral',
] as const;

export type U3141NasTruthDocumentType = (typeof U3141_NAS_TRUTH_DOCUMENT_TYPES)[number];

export function u3141IsNasTruthDocumentType(documentType: string | null | undefined): boolean {
  if (!documentType) return false;
  return (U3141_NAS_TRUTH_DOCUMENT_TYPES as readonly string[]).includes(documentType);
}

/**
 * Déduit un MIME à partir de l’en-tête HTTP et/ou des premiers octets.
 */
export function u3141InferDocumentMime(
  buffer: ArrayBuffer,
  headerContentType?: string | null
): string {
  const header = (headerContentType ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
  if (header === 'application/pdf' || header === 'text/html' || header.startsWith('text/')) {
    return header === 'text/html' || header.startsWith('text/html') ? 'text/html' : header;
  }
  if (header.includes('html')) return 'text/html';
  if (header.includes('pdf')) return 'application/pdf';

  const bytes = new Uint8Array(buffer);
  if (bytes.length >= 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
    return 'application/pdf';
  }
  const head = new TextDecoder('utf-8', { fatal: false }).decode(bytes.slice(0, Math.min(256, bytes.length)));
  const trimmed = head.trimStart().toLowerCase();
  if (trimmed.startsWith('<!doctype html') || trimmed.startsWith('<html') || trimmed.startsWith('<head')) {
    return 'text/html';
  }
  return header || 'application/octet-stream';
}

export function u3141IsHtmlMime(mime: string): boolean {
  return mime.toLowerCase().includes('html');
}

export type U3141OpenDocumentPdfFileDeps = {
  createObjectUrl?: (blob: Blob) => string;
  revokeObjectUrl?: (url: string) => void;
  openWindow?: (url: string, target?: string, features?: string) => Window | null;
  scheduleRevoke?: (fn: () => void, ms: number) => void;
  revokeDelayMs?: number;
};

export type U3141DocumentPdfFilePayload = {
  buffer: ArrayBuffer;
  contentType?: string | null;
};

/**
 * Construit un Blob + object URL à partir du buffer pdf-file.
 */
export function u3141BuildDocumentPdfFileObjectUrl(
  payload: U3141DocumentPdfFilePayload,
  deps: U3141OpenDocumentPdfFileDeps = {}
): { objectUrl: string; mime: string; blob: Blob } {
  const mime = u3141InferDocumentMime(payload.buffer, payload.contentType);
  const blob = new Blob([payload.buffer], { type: mime });
  const createObjectUrl = deps.createObjectUrl ?? ((b: Blob) => URL.createObjectURL(b));
  return { objectUrl: createObjectUrl(blob), mime, blob };
}

/**
 * Ouvre le document pdf-file dans un nouvel onglet (PDF ou HTML).
 * Révoque l’object URL après un délai pour laisser le viewer charger.
 */
export function u3141OpenDocumentPdfFileInNewTab(
  payload: U3141DocumentPdfFilePayload,
  deps: U3141OpenDocumentPdfFileDeps = {}
): void {
  const revokeObjectUrl = deps.revokeObjectUrl ?? ((url: string) => URL.revokeObjectURL(url));
  const openWindow =
    deps.openWindow ??
    ((url: string, target?: string, features?: string) => window.open(url, target, features));
  const scheduleRevoke =
    deps.scheduleRevoke ??
    ((fn: () => void, ms: number) => {
      window.setTimeout(fn, ms);
    });
  const revokeDelayMs = deps.revokeDelayMs ?? 60_000;

  const { objectUrl } = u3141BuildDocumentPdfFileObjectUrl(payload, deps);
  try {
    const opened = openWindow(objectUrl, '_blank', 'noopener,noreferrer');
    if (!opened) {
      throw new Error('POPUP_BLOCKED');
    }
  } finally {
    scheduleRevoke(() => revokeObjectUrl(objectUrl), revokeDelayMs);
  }
}

/**
 * Décode le buffer en texte HTML (historique cadastral).
 */
export function u3141DecodeDocumentPdfFileAsText(payload: U3141DocumentPdfFilePayload): string {
  return new TextDecoder('utf-8', { fatal: false }).decode(payload.buffer);
}
